"use client";

import React, { useState, useEffect } from "react";
import { Button } from "../../ui/button";
import { ArrowLeft } from "lucide-react";
import { getFY_YEAR } from "@/utils/sharepoint-env";
import { MultiSelectDropdown } from "../../ui/multi-select-dropdown";

interface CCESubstantiveProps {
  onBack?: () => void;
  setActiveSection?: (section: string) => void;
  procedureId?: string; // Optional: if provided, show only this procedure's content
}

type LedgerEntry = { ledger_name: string; latest_date: string; amount: number };

type BankAccountRow = {
  bankName: string;
  accountNumber: string;
  accountType: string;
  ledgerLinked: string[];
  currency: string;
  confirmationId: string;
};

const ACCOUNT_TYPES = [
  "Current",
  "Savings",
  "Fixed Deposit",
  "Cash Credit",
  "Escrow",
  "Unpaid Dividend",
  "PCFC",
  "Capital Gain Account",
  "Sweep Deposit",
  "EEFC",
  "Recurring Deposit",
];

export default function CCESubstantive({ onBack, setActiveSection, procedureId }: CCESubstantiveProps) {
  // If no onBack is provided but setActiveSection is, create a back handler
  const handleBack = onBack || (() => {
    if (setActiveSection) {
      setActiveSection("cce-substantive");
    }
  });
  const [ledgerOptions, setLedgerOptions] = useState<string[]>([]);
  const [ledgerData, setLedgerData] = useState<LedgerEntry[]>([]);
  const [confirmationOptions, setConfirmationOptions] = useState<string[]>([]);
  const [isLoadingLedgers, setIsLoadingLedgers] = useState(false);
  const [isLoadingTable, setIsLoadingTable] = useState(false);
  const [isLoadingConfirmations, setIsLoadingConfirmations] = useState(false);
  const [rows, setRows] = useState<BankAccountRow[]>([
    { bankName: "", accountNumber: "", accountType: "", ledgerLinked: [], currency: "", confirmationId: "" },
  ]);

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      { bankName: "", accountNumber: "", accountType: "", ledgerLinked: [], currency: "", confirmationId: "" },
    ]);
  };

  const updateRow = (index: number, patch: Partial<BankAccountRow>) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const loadLedgerNames = async () => {
    setIsLoadingLedgers(true);
    try {
      if (window.sharePointAPI?.loadBankAccountLedgers) {
        const result = await window.sharePointAPI.loadBankAccountLedgers();
        
        if (result?.success && result.data) {
          const data = result.data as LedgerEntry[];
          setLedgerData(data);
          const ledgerNames = data.map((item) => item.ledger_name).filter(Boolean);
          const unique = Array.from(new Set(ledgerNames)).sort();
          setLedgerOptions(unique);
        } else {
          alert(result?.error || 'Failed to load ledger names');
        }
      } else {
        alert('SharePoint API not available');
      }
    } catch (error) {
      console.error("Error loading ledger names:", error);
      alert(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoadingLedgers(false);
    }
  };

  const loadConfirmations = async () => {
    setIsLoadingConfirmations(true);
    try {
      if ((window as any).sharePointAPI?.downloadClientJson) {
        const result = await (window as any).sharePointAPI.downloadClientJson("Confirmation_Response.json");
        if (result?.success && result.data && typeof result.data === "object") {
          const ids = Object.keys(result.data).filter((id) => id.startsWith("CCE_")).sort();
          setConfirmationOptions(ids);
        } else {
          setConfirmationOptions([]);
        }
      } else {
        setConfirmationOptions([]);
      }
    } catch (error) {
      console.error("Error loading confirmations:", error);
      setConfirmationOptions([]);
    } finally {
      setIsLoadingConfirmations(false);
    }
  };

  const loadTableData = async () => {
    setIsLoadingTable(true);
    try {
      if ((window as any).sharePointAPI?.downloadClientJson) {
        const result = await (window as any).sharePointAPI.downloadClientJson("Execution_CCE_CashTestingInp.json");
        
        if (result?.success && result.data) {
          // Convert from snake_case (saved format) to camelCase (component format)
          const loadedRows: BankAccountRow[] = Array.isArray(result.data)
            ? result.data.map((item: any) => ({
                bankName: item.bank_name || "",
                accountNumber: item.account_number !== undefined ? String(item.account_number) : "",
                accountType: item.account_type || "",
                ledgerLinked: Array.isArray(item.ledger_accounts_linked) ? item.ledger_accounts_linked : [],
                currency: item.currency || "",
                confirmationId: item.confirmation_id || "",
              }))
            : [];
          
          if (loadedRows.length > 0) {
            setRows(loadedRows);
          }
        } else {
          // File doesn't exist yet or failed to load - keep default empty row
          console.log("No existing data found, starting with empty row");
        }
      } else {
        console.warn("SharePoint API not available");
      }
    } catch (error) {
      console.error("Error loading table data:", error);
      // On error, keep the default empty row
    } finally {
      setIsLoadingTable(false);
    }
  };

  // Compute row amount: sum of amounts for latest date for selected ledgers (from TB mapping)
  const getRowAmount = (ledgerLinked: string[]) => {
    if (!ledgerLinked.length || !ledgerData.length) return null;
    const total = ledgerData
      .filter((e) => ledgerLinked.includes(e.ledger_name))
      .reduce((sum, e) => sum + (e.amount ?? 0), 0);
    return total;
  };

  // Automatically load ledger names, confirmations, and table data when component mounts
  useEffect(() => {
    loadLedgerNames();
    loadConfirmations();
    loadTableData();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Cash & Cash Equivalents - Substantive Procedures</h2>
          <p className="text-gray-400">Cash testing</p>
        </div>
        {(onBack || setActiveSection) && (
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
        )}
      </div>

      <div className="relative z-40 rounded-lg border border-white/10 bg-black/40 p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">i. Bank Accounts</h3>
          <div className="flex items-center gap-2">
            {isLoadingTable && (
              <span className="text-xs text-white/60">Loading table data...</span>
            )}
            <Button size="sm" variant="outline" onClick={loadLedgerNames} disabled={isLoadingLedgers}>
              {isLoadingLedgers ? "Loading Ledgers..." : "Load Ledger Names"}
            </Button>
            <Button size="sm" onClick={addRow}>Add Row</Button>
          </div>
        </div>

        <div className="overflow-x-auto overflow-y-visible">
          <table className="w-full text-left text-xs text-white/80 border-collapse">
            <thead className="text-white/70">
              <tr>
                <th className="border-b border-white/10 py-2 pr-2">Bank Name</th>
                <th className="border-b border-white/10 py-2 pr-2">Account Number</th>
                <th className="border-b border-white/10 py-2 pr-2">Account Type</th>
                <th className="border-b border-white/10 py-2 pr-2">Ledger Accounts Linked</th>
                <th className="border-b border-white/10 py-2 pr-2">Currency</th>
                <th className="border-b border-white/10 py-2 pr-2">Amount</th>
                <th className="border-b border-white/10 py-2 pr-2">Confirmation</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={`row-${idx}`}>
                  <td className="border-b border-white/10 py-2 pr-2">
                    <input
                      type="text"
                      className="w-full bg-black/40 border border-white/10 rounded-md px-2 py-1 text-white/80 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={row.bankName || ""}
                      onChange={(e) => updateRow(idx, { bankName: e.target.value })}
                      disabled={false}
                    />
                  </td>
                  <td className="border-b border-white/10 py-2 pr-2">
                    <input
                      type="text"
                      className="w-full bg-black/40 border border-white/10 rounded-md px-2 py-1 text-white/80 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={row.accountNumber || ""}
                      onChange={(e) => updateRow(idx, { accountNumber: e.target.value })}
                      disabled={false}
                    />
                  </td>
                  <td className="border-b border-white/10 py-2 pr-2">
                    <select
                      className="w-full bg-black/40 border border-white/10 rounded-md px-2 py-1 text-white/80 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={row.accountType || ""}
                      onChange={(e) => updateRow(idx, { accountType: e.target.value })}
                    >
                      <option value="" className="bg-black">Select type</option>
                      {ACCOUNT_TYPES.map((t) => (
                        <option key={t} value={t} className="bg-black">{t}</option>
                      ))}
                    </select>
                  </td>
                  <td className="border-b border-white/10 py-2 pr-2">
                    <div className="min-w-[250px]">
                      <MultiSelectDropdown
                        options={ledgerOptions}
                        value={row.ledgerLinked || []}
                        onChange={(selected) => updateRow(idx, { ledgerLinked: selected })}
                        placeholder={isLoadingLedgers ? "Loading ledger names..." : ledgerOptions.length === 0 ? "Load ledger names to populate options" : "Select ledger accounts..."}
                        loading={isLoadingLedgers}
                        disabled={isLoadingLedgers || ledgerOptions.length === 0}
                      />
                    </div>
                  </td>
                  <td className="border-b border-white/10 py-2 pr-2">
                    <input
                      type="text"
                      className="w-full bg-black/40 border border-white/10 rounded-md px-2 py-1 text-white/80 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={row.currency || ""}
                      onChange={(e) => updateRow(idx, { currency: e.target.value })}
                      disabled={false}
                    />
                  </td>
                  <td className="border-b border-white/10 py-2 pr-2">
                    <span className="text-white/80">
                      {(() => {
                        const amt = getRowAmount(row.ledgerLinked);
                        return amt != null ? Math.round(amt).toLocaleString("en-IN", { maximumFractionDigits: 0 }) : "—";
                      })()}
                    </span>
                  </td>
                  <td className="border-b border-white/10 py-2 pr-2">
                    <select
                      className="w-full min-w-[140px] bg-black/40 border border-white/10 rounded-md px-2 py-1 text-white/80 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={row.confirmationId || ""}
                      onChange={(e) => updateRow(idx, { confirmationId: e.target.value })}
                      disabled={isLoadingConfirmations}
                    >
                      <option value="" className="bg-black">None</option>
                      {confirmationOptions.map((id) => (
                        <option key={id} value={id} className="bg-black">{id}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Generate & Upload Section */}
        <div className="flex items-center gap-3 pt-2">
          <Button
            onClick={async () => {
              try {
                // Get FY_YEAR from database dynamically
                const fyYear = getFY_YEAR();
                if (!fyYear) {
                  alert(" FY_YEAR is not configured. Please ensure you have selected an engagement.");
                  return;
                }

                const payload = rows.map((r) => {
                  const amount = getRowAmount(r.ledgerLinked);
                  return {
                    bank_name: r.bankName,
                    account_number: Number.isFinite(parseInt(r.accountNumber)) ? parseInt(r.accountNumber) : r.accountNumber,
                    account_type: r.accountType,
                    ledger_accounts_linked: r.ledgerLinked,
                    currency: r.currency,
                    amount: amount != null ? amount : 0,
                    confirmation_id: r.confirmationId || undefined,
                  };
                });
                const jsonString = JSON.stringify(payload, null, 2);
                const base64Content = btoa(unescape(encodeURIComponent(jsonString)));
                if (window.sharePointAPI?.uploadFile) {
                  const result = await window.sharePointAPI.uploadFile({
                    fileContent: base64Content,
                    fileName: "Execution_CCE_CashTestingInp.json",
                    referenceValue: "",
                    folderName: "juggernaut",
                    fyYear: fyYear,
                    sectionList: ["Execution_CCE_CashTestingInp"]
                  });
                  if (result?.success) {
                    alert(" Uploaded to SharePoint successfully.");
                  } else {
                    alert(` Upload failed: ${result?.error || "Unknown error"}`);
                  }
                } else {
                  alert("SharePoint API not available");
                }
              } catch (e) {
                alert(`Error: ${e instanceof Error ? e.message : "Unknown"}`);
              }
            }}
          >
            Upload to SharePoint
          </Button>
        </div>

        {/* Execute Cash Testing */}
        <div className="relative z-30 mt-4 rounded-lg border border-white/10 bg-black/30 p-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-white">Run Cash Testing</div>
              <div className="text-xs text-white/60">Generates Excel and uploads to SharePoint (client folder), then updates db.json</div>
            </div>
            <Button
              onClick={async () => {
                try {
                  if (window.payroll?.run) {
                    // CCE Bank Accounts: AS → 20023, 20008; Ind AS → 20014, 20049
                    let fsSubLineIds: number[] = [20023, 20008];
                    try {
                      const frfResult = await (window as any).sharePointAPI?.getFrf?.();
                      const frf = frfResult?.success ? frfResult.frf : null;
                      if (frf === "Indian Accounting Standards (Ind AS)") {
                        fsSubLineIds = [20014, 20049];
                      }
                    } catch {
                      // keep default AS ids
                    }
                    const res = await window.payroll.run("execute_cce_cash_testing", {
                      inputFiles: [],
                      options: { fs_sub_line_ids: fsSubLineIds }
                    });
                    if (res.ok) {
                      alert(" Cash Testing completed and uploaded to SharePoint.");
                    } else {
                      alert(" Failed to start Cash Testing");
                    }
                  } else {
                    alert("SharePoint/Payroll runner not available");
                  }
                } catch (e) {
                  alert(`Error: ${e instanceof Error ? e.message : "Unknown"}`);
                }
              }}
            >
              Run Cash Testing
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}



